import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, {
  BugsnagErrorBoundary
} from '@bugsnag/plugin-react';
import CircularProgress from '@mui/material/CircularProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import React, { Suspense } from 'react';
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch
} from 'react-router-dom';
import './App.css';
import Home from './Host/Home';
import useViewport from './Stryder/hooks/useViewport';
import PrivateRoute from './auth/PrivateRoute';
import { IdentityRoutes } from './common/IdentityRoutes';
import { practiceRoute } from './common/PracticeRoutes';
import { UiRoutes, sampleLtiRoute } from './common/UiRoutes';
import config from './config';
import useThemes from './hooks/useThemes';
import Centered from './shared/Centered';
import SplashScreen from './shared/SplashScreen';

// Authenticated system.
const IdentityNotSupported = React.lazy(
  () => import('./membership/IdentityNotSupported')
);

const authRoutes = [
  IdentityRoutes.REGISTER,
  IdentityRoutes.VERIFY_EMAIL,
  IdentityRoutes.REQUEST_VERIFICATION,
  IdentityRoutes.REQUEST_PASSWORD,
  IdentityRoutes.RESET_PASSWORD,
  IdentityRoutes.INVITATION
];

// Stryder views.
const [Handoff] = [
  React.lazy(() => import('./Stryder/components/Handoff/Handoff'))
];

// Hosting samples.
const [HostSamples, LtiOAuth2, EmbedSample, OpenAccessSample, DeviceIssues] = [
  React.lazy(() => import('./Host/HostSamples')),
  React.lazy(() => import('./Host/HostSamplesLti')),
  React.lazy(() => import('./Host/EmbedSample')),
  React.lazy(() => import('./Host/OpenAccessSample')),
  React.lazy(() => import('./Host/DeviceIssues'))
];

// Practice, admin views.
const [Practice, Admin, Trial] = [
  React.lazy(() => import('./Practice/Practice')),
  React.lazy(() => import('./Admin/Admin')),
  React.lazy(() => import('./Trial/Trial'))
];

Bugsnag.start({
  apiKey:
    process.env.REACT_APP_BUGSNAG_API_KEY || '00000111112222233333444445555566',
  plugins: [new BugsnagPluginReact()],
  releaseStage: config.ENV,
  enabledReleaseStages: ['dev', 'staging', 'live']
});
const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(
  React
) as BugsnagErrorBoundary;

/**
 * Top-level application component.
 */
function App() {
  useViewport();
  const { muiTheme } = useThemes();
  const fontFamily = muiTheme.typography.fontFamily;
  muiTheme.typography.fontFamily =
    muiTheme.typography.h1.fontFamily =
    muiTheme.typography.h2.fontFamily =
    muiTheme.typography.h3.fontFamily =
    muiTheme.typography.h4.fontFamily =
    muiTheme.typography.h5.fontFamily =
    muiTheme.typography.h6.fontFamily =
    muiTheme.typography.subtitle1.fontFamily =
    muiTheme.typography.subtitle2.fontFamily =
    muiTheme.typography.body1.fontFamily =
    muiTheme.typography.body2.fontFamily =
    muiTheme.typography.caption.fontFamily =
    muiTheme.typography.button.fontFamily =
    muiTheme.typography.overline.fontFamily =
      fontFamily;

  return (
    <ErrorBoundary>
        <ThemeProvider theme={muiTheme}>
          <Router>
            <Suspense
              fallback={
                <Centered>
                  <CircularProgress size={32} />
                </Centered>
              }
            >
              <Switch>
                <Route exact path="/" component={Home} />

                {/*
                 * Authentication system.
                 */}
                {authRoutes.map((path) => (
                  <Route
                    key={`${path}`}
                    path={path}
                    exact
                    component={IdentityNotSupported}
                  />
                ))}

                {/* Logged-in practice views. */}
                <Route path={practiceRoute} component={Practice} />
                <Route path={UiRoutes.TRIAL} component={Trial} />
                <PrivateRoute path={UiRoutes.ADMIN} component={Admin} />

                {/*
                 * Demo LTI 1.3 / OAuth2 launcher to demonstrate how the Viewer
                 * can be included in a host system. This is comprised of:
                 *  - /launch-viewer: Page that hosts the viewer iframe
                 *  - /launch-viewer/:instanceId: Launch page, from within iframe,
                 *    that performs a POST to the /auth/v/launch endpoint with a
                 *    suitable id_token.
                 *  - privatkeys.json: Used by both server and front-end to sign
                 *    the supplied id_token.
                 *  - HostAuthService: A server endpoint that delivers the jwks
                 *    signing keys.
                 */}
                <Route path={sampleLtiRoute} component={LtiOAuth2} />
                <Route path={UiRoutes.SAMPLE_EMBED} component={EmbedSample} />
                <Route path={UiRoutes.SAMPLE_OPEN_ACCESS} component={OpenAccessSample} />

                {/* Sample local/OAuth2/hmac/apikey authenticated requests. */}
                <Route path={UiRoutes.SAMPLES} component={HostSamples} />
                <Route path={UiRoutes.SAMPLE_HOME}>
                  <Redirect to={UiRoutes.SAMPLES.replace(':auth', 'oauth2')} />
                </Route>
                <Route
                  path={UiRoutes.SAMPLE_DEVICE_ISSUES}
                  component={DeviceIssues}
                />

                {/* Standalone viewer launched with code. */}
                <Route path={UiRoutes.HANDOFF} component={Handoff} />
              </Switch>
            </Suspense>
          </Router>
        </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
